/**
 * Instagram Reels Carousel — Inline Video Playback
 * Plays video directly inside the card when clicked. No popups or redirects.
 */
(() => {
  'use strict';

  function initInstagramReels() {
    const sectionElements = document.querySelectorAll('[data-ig-reels]');
    if (!sectionElements || sectionElements.length === 0) return;

    sectionElements.forEach((section) => {
      try {
        setupCarousel(section);
        setupCardVideos(section);
      } catch (err) {
        console.error('[Instagram Reels] Setup error:', err);
      }
    });
  }

  function setupCarousel(section) {
    const track = section.querySelector('[data-ig-track]');
    const prevBtn = section.querySelector('[data-ig-prev]');
    const nextBtn = section.querySelector('[data-ig-next]');

    if (!track) return;

    const scrollAmount = () => {
      const card = track.querySelector('[data-ig-card]');
      return card ? card.offsetWidth + 20 : 280;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    }

    const updateNavState = () => {
      if (!prevBtn || !nextBtn) return;
      const isAtStart = track.scrollLeft <= 5;
      const isAtEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
      prevBtn.disabled = isAtStart;
      nextBtn.disabled = isAtEnd;
    };

    track.addEventListener('scroll', updateNavState, { passive: true });
    updateNavState();
  }

  function setupCardVideos(section) {
    const cards = Array.from(section.querySelectorAll('[data-ig-card]'));
    if (!cards || cards.length === 0) return;

    cards.forEach((card) => {
      const video = card.querySelector('[data-ig-video]');
      const soundBtn = card.querySelector('[data-ig-sound]');
      const playBtn = card.querySelector('[data-ig-play], .ig-reels__ig-play-btn');
      const heartBtn = card.querySelector('[data-ig-heart]');
      const bookmarkBtn = card.querySelector('[data-ig-bookmark]');
      const likeCountEl = card.querySelector('[data-ig-like-count]');
      const extLinks = card.querySelectorAll('[data-ig-ext-link]');

      if (!video) return;

      // Ensure video is initially muted so mobile browsers allow instant autoplay on tap
      video.muted = true;

      const playVideo = async () => {
        // Pause all other playing reels first
        cards.forEach((otherCard) => {
          if (otherCard !== card) {
            const otherVideo = otherCard.querySelector('[data-ig-video]');
            if (otherVideo && !otherVideo.paused) {
              otherVideo.pause();
            }
            otherCard.classList.remove('is-playing');
          }
        });

        // 100% On-Demand Loading: Only fetch video source on click!
        const dataSrc = video.getAttribute('data-src');
        if (!video.src || video.src === '' || video.src === window.location.href) {
          if (dataSrc) {
            video.src = dataSrc;
            video.load();
          }
        }

        if (!dataSrc && (!video.src || video.src === '' || video.src === window.location.href)) {
          const extUrl = card.getAttribute('data-url');
          if (extUrl) {
            window.open(extUrl, '_blank', 'noopener,noreferrer');
          }
          return;
        }

        // Visually mark as playing immediately so user sees instant response
        card.classList.add('is-playing');

        try {
          await video.play();
        } catch (err) {
          console.warn('[Instagram Reels] Play error, retrying muted:', err);
          video.muted = true;
          try {
            await video.play();
          } catch (retryErr) {
            console.error('[Instagram Reels] Could not play video:', retryErr);
            card.classList.remove('is-playing');
          }
        }
      };

      const pauseVideo = () => {
        video.pause();
        card.classList.remove('is-playing');
      };

      const togglePlayback = (e) => {
        // Skip if sound toggle, heart, bookmark, or external link was clicked
        if (e && e.target && (
          e.target.closest('[data-ig-sound]') ||
          e.target.closest('[data-ig-heart]') ||
          e.target.closest('[data-ig-bookmark]') ||
          e.target.closest('[data-ig-ext-link]')
        )) {
          return;
        }

        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (video.paused || video.ended || !card.classList.contains('is-playing')) {
          playVideo();
        } else {
          pauseVideo();
        }
      };

      // Register click on the card and play button
      card.addEventListener('click', togglePlayback);
      if (playBtn) {
        playBtn.addEventListener('click', togglePlayback);
      }

      // Heart like button toggle
      if (heartBtn) {
        heartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          heartBtn.classList.toggle('is-liked');
          if (likeCountEl) {
            let count = parseInt(likeCountEl.textContent, 10) || 0;
            count = heartBtn.classList.contains('is-liked') ? count + 1 : Math.max(0, count - 1);
            likeCountEl.textContent = count;
          }
        });
      }

      // Bookmark button toggle
      if (bookmarkBtn) {
        bookmarkBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          bookmarkBtn.classList.toggle('is-saved');
        });
      }

      // Sound button toggle
      if (soundBtn) {
        soundBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          if (video.muted) {
            soundBtn.classList.remove('is-unmuted');
            soundBtn.setAttribute('aria-label', 'Unmute sound');
          } else {
            soundBtn.classList.add('is-unmuted');
            soundBtn.setAttribute('aria-label', 'Mute sound');
          }
        });
      }

      // External links stopPropagation
      if (extLinks && extLinks.length > 0) {
        extLinks.forEach((link) => {
          link.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        });
      }

      // Sync state if video paused or ended externally
      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
      });

      video.addEventListener('play', () => {
        card.classList.add('is-playing');
      });

      video.addEventListener('ended', () => {
        card.classList.remove('is-playing');
      });
    });
  }

  // Run on load and immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInstagramReels);
  } else {
    initInstagramReels();
  }

  // Also support Shopify Theme Customizer events
  document.addEventListener('shopify:section:load', initInstagramReels);
  document.addEventListener('shopify:section:select', initInstagramReels);
})();
